import { useCallback, useRef, useState } from 'react';
import {
  variantsCombosService,
  type UpsertVariantComboGroupRequest,
  type VariantComboGroupView,
} from '../../../../services/variants-combos/variants-combos.service';
import { type UiGroup, toModelTree } from '../../components/settings/options-builder/options-builder.utils';

// Architectural summary: Owns the per-product variants/combos editor state for ניהול מוצרים (Rule 32).
export function useProductCompositionEditor(tenantId: string, employeeSession: string | null) {
  const [groups, setGroups] = useState<VariantComboGroupView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const productIdRef = useRef<number | null>(null);

  const loadComposition = useCallback(
    async (productId: number) => {
      if (!employeeSession) {
        return;
      }
      productIdRef.current = productId;
      setIsLoading(true);
      try {
        const loaded = await variantsCombosService.listForProduct(tenantId, productId, employeeSession);
        if (productIdRef.current === productId) {
          setGroups(loaded);
        }
      } finally {
        if (productIdRef.current === productId) {
          setIsLoading(false);
        }
      }
    },
    [employeeSession, tenantId],
  );

  const clearComposition = useCallback(() => {
    productIdRef.current = null;
    setGroups([]);
    setIsLoading(false);
  }, []);

  const saveGroup = useCallback(
    async (payload: UpsertVariantComboGroupRequest) => {
      if (!employeeSession) {
        return;
      }
      setIsSaving(true);
      try {
        await variantsCombosService.upsertGroup(tenantId, payload, employeeSession);
      } finally {
        setIsSaving(false);
      }
    },
    [employeeSession, tenantId],
  );

  const removeGroup = useCallback(
    async (groupId: number) => {
      if (!employeeSession) {
        return;
      }
      await variantsCombosService.deleteGroup(tenantId, groupId, employeeSession);
    },
    [employeeSession, tenantId],
  );

  // Architectural summary: Commits the whole Options Builder draft for a product in ONE atomic request —
  const commitDraft = useCallback(
    async (productId: number, uiGroups: UiGroup[]): Promise<void> => {
      if (!employeeSession) {
        return;
      }
      const nodes = uiGroups.map((g, i) => toModelTree(g, i));
      if (hasSameComposition(groups, nodes)) {
        return;
      }
      setIsSaving(true);
      try {
        await variantsCombosService.batchUpsertProductGroups(tenantId, productId, nodes, employeeSession);
      } finally {
        setIsSaving(false);
      }
    },
    [employeeSession, groups, tenantId],
  );

  const copyGroupToProducts = useCallback(
    async (productIds: number[], itemIds: number[], group: UiGroup): Promise<void> => {
      if (!employeeSession) {
        return;
      }
      const node = toModelTree(group, 0);
      await variantsCombosService.copyGroupToProducts(tenantId, productIds, itemIds, node, employeeSession);
    },
    [employeeSession, tenantId],
  );

  const loadAllGroups = useCallback(
    async (): Promise<VariantComboGroupView[]> => {
      if (!employeeSession) {
        return [];
      }
      return variantsCombosService.listAllForTenant(tenantId, employeeSession);
    },
    [employeeSession, tenantId],
  );

  const loadLimitItemsGroups = useCallback(
    async (): Promise<VariantComboGroupView[]> => {
      if (!employeeSession) {
        return [];
      }
      return variantsCombosService.listForLimitItems(tenantId, employeeSession);
    },
    [employeeSession, tenantId],
  );


  const applyVariantComboGroupUpserted = useCallback((productId: number, group: VariantComboGroupView) => {
    if (productId !== productIdRef.current) {
      return;
    }
    setGroups((prev) => mergeById(prev, group));
  }, []);

  const applyVariantComboGroupRemoved = useCallback((productId: number, groupId: number) => {
    if (productId !== productIdRef.current) {
      return;
    }
    setGroups((prev) => prev.filter((g) => g.id !== groupId));
  }, []);

  return {
    groups,
    isLoadingComposition: isLoading,
    isSavingComposition: isSaving,
    loadComposition,
    clearComposition,
    saveGroup,
    removeGroup,
    loadAllGroups,
    loadLimitItemsGroups,
    commitDraft,
    copyGroupToProducts,
    applyVariantComboGroupUpserted,
    applyVariantComboGroupRemoved,
  };
}

function mergeById<T extends { id: number }>(list: T[], incoming: T): T[] {
  const exists = list.some((item) => item.id === incoming.id);
  return exists ? list.map((item) => (item.id === incoming.id ? incoming : item)) : [...list, incoming];
}

function hasSameComposition(groups: VariantComboGroupView[], draftNodes: UiGroupNode[]): boolean {
  const currentNodes = groups.map((g, i) => toUiGroupNodeFromView(g, i));
  return JSON.stringify(currentNodes) === JSON.stringify(draftNodes);
}

type UiGroupNode = ReturnType<typeof toModelTree>;

function toUiGroupNodeFromView(group: VariantComboGroupView, sortOrder: number): UiGroupNode {
  return {
    id: group.id,
    groupType: group.groupType,
    name: group.name.trim(),
    pickLimit: group.pickLimit,
    freeLimit: group.freeLimit,
    minPick: group.minPick,
    sortOrder,
    items: group.items.map((item, index) => ({
      id: item.id,
      name: item.name.trim(),
      priceWithinFreeLimit: item.priceWithinFreeLimit,
      priceAfterFreeLimit: item.priceAfterFreeLimit,
      isDefault: item.isDefault,
      kds: item.kds,
      maxPick: item.maxPick ?? null,
      sortOrder: index,
      printerIds: item.printerIds ?? [],
      areaPrintRuleId: item.areaPrintRuleId ?? null,
      childGroups: (item.childGroups ?? []).map((childGroup, childIndex) => toUiGroupNodeFromView(childGroup, childIndex)),
    })),
  };
}
